import React, { useMemo } from "react";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import StatisticsCard from "../card/StatisticsCard";
import {
  ComputerIcon,
  MoneyIcon,
  MouseIcon,
  VideoIcon,
} from "../../icons/customIcons";

// Helper function to format numbers with commas
const formatNumber = (num: number): string => {
  return num.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

// Helper function to format currency
const formatCurrency = (amount: number): string => {
  return `€ ${amount.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

interface MetricsSummary {
  totalImpressions: number;
  totalClicks: number;
  totalCost: number;
  avgCTR: number;
  avgCPC: number;
  avgCPM: number;
  totalConversions: number;
  totalCompleteViews: number;
  avgVTR: number;
  avgCPV: number;
  platforms: Record<
    string,
    {
      impressions: number;
      clicks: number;
      cost: number;
      ctr: number;
      cpm: number;
      cpc: number;
    }
  >;
  topCampaigns: {
    byClicks: Array<{ id: string; name: string; value: number }>;
    byImpressions: Array<{ id: string; name: string; value: number }>;
    bySpend: Array<{ id: string; name: string; value: number }>;
  } | null;
}

interface StatisticsSectionProps {
  summary?: MetricsSummary;
  columns?: 2 | 4;
  fillHeight?: boolean;
}

const StatisticsSection: React.FC<StatisticsSectionProps> = ({
  summary,
  columns = 4,
  fillHeight = false,
}) => {
  const { palette } = useTheme();
  const { t } = useTranslation();

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    if (!summary) {
      return {
        impressions: 0,
        clicks: 0,
        cost: 0,
        views: 0,
      };
    }

    return {
      impressions: summary.totalImpressions,
      clicks: summary.totalClicks,
      cost: summary.totalCost,
      views: summary.totalCompleteViews,
    };
  }, [summary]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr", // 1 card per row on extra small screens
          sm: "1fr 1fr", // 2 cards per row on small screens
          md: columns === 2 ? "1fr 1fr" : "1fr 1fr 1fr 1fr",
        },
        gridTemplateRows: fillHeight ? "1fr 1fr" : undefined,
        gap: "16px",
        width: "100%",
        height: fillHeight ? "100%" : undefined,
        overflow: "hidden",
      }}
    >
      <StatisticsCard
        value={formatCurrency(summaryMetrics.cost)}
        text={t("home.totalInvestment")}
        icon={<MoneyIcon color={palette.info.main} size={24} />}
      />
      <StatisticsCard
        value={formatNumber(summaryMetrics.impressions)}
        text={t("home.totalImpressions")}
        icon={<VideoIcon color={palette.info.main} size={24} />}
      />
      <StatisticsCard
        value={formatNumber(summaryMetrics.clicks)}
        text={t("home.clicks")}
        icon={<MouseIcon color={palette.info.main} size={24} />}
      />
      <StatisticsCard
        value={formatNumber(summaryMetrics.views)}
        text={t("home.totalViews")}
        icon={<ComputerIcon color={palette.info.main} size={24} />}
      />
    </Box>
  );
};

export default StatisticsSection;
